import { Box, Typography } from "@mui/material";
import { IPaginatedList } from "Interfaces/PaginatedList";
import {
  DataGrid,
  esES,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "components/Modal";
import { useAppSelector } from "redux/store";
import styles from "scss/Table.module.scss";
import { ISelectOption } from "Interfaces/Utils";
import { IOperationState, setPageIndex, setPageSize, setSort } from "redux/features/operationSlice";
import { IOperation } from "Models/Operation";
import { getFormalDate } from "utils/formatDate";
import { useIntl } from "react-intl";
import { LanguageContext } from "LanguageProvider";
import { getMuiLocaleText } from "utils/getMUILocaleText";

interface OperationTableProps {
  paginatedQuery?: IPaginatedList<IOperation>;
  isLoading: boolean;
  deleteOperation: (operationId: string) => void;
  companies: ISelectOption<string>[];
  pointsOfSale: ISelectOption<string>[];
  documentType: ISelectOption<string>[];
}

export const Table = ({
  paginatedQuery,
  isLoading,
  deleteOperation,
  companies,
  pointsOfSale,
  documentType,
}: OperationTableProps) => {
  const { pageSize, sort, pageIndex } = useAppSelector(
    (state: { operationState: IOperationState }) => state.operationState
  );
  const { user } = useAppSelector((state) => state.userLoggedState);
  const { formatMessage } = useIntl();
  const languageContext = useContext(LanguageContext);
  const locale = languageContext?.locale;
  const localeText = useMemo(() => getMuiLocaleText(locale ?? "es", formatMessage), [locale, formatMessage]);
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [operationIdToDelete, setOperationIdToDelete] = useState<string | undefined>(
    undefined
  );

  const IMAGE_SERVER = process.env.REACT_APP_IMAGES_ENDPOINT;

  const rows = paginatedQuery?.data ?? [];


  const handleSort = (newSortModel: GridSortModel) => {
    dispatch(setSort(newSortModel));
  };

  const handleChangePage = (newPage: number) => {
    dispatch(setPageIndex(newPage));
  };

  const handleChangeRowsPerPage = (newPageSize: number) => {
    dispatch(setPageSize(newPageSize));
  };

  const onDeleteOpenModal = (operationId: string) => {
    setOpenDeleteModal(true);
    setOperationIdToDelete(operationId);
  };

  const onCloseDeleteModal = () => {
    setOperationIdToDelete(undefined);
    setOpenDeleteModal(false);
  };

  const onAcceptDeleteModal = () => {
    if (operationIdToDelete) {
      deleteOperation(operationIdToDelete);
    }
    setOperationIdToDelete(undefined);
    setOpenDeleteModal(false);
  };

  const onEdit = useCallback(
    (operationId: string) => {
      navigate("/operations/" + operationId);
    },
    [navigate]
  );
  
  
  const columns = useMemo<GridColDef[]>(
    () => [    
      ...(user?.role === "SUPERADMIN" // Condición para incluir la columna
        ? [
            {
              field: "company",
              headerName: formatMessage({ id: "operation.fields.Company" }),
              flex: 1,
              renderCell(params: GridRenderCellParams) {
                return (
                  <Typography
                    sx={{
                      color: "#111827",
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "21px",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {companies.find((company) => company.value === params.row.companyId)?.label}
                  </Typography>
                );
              },
            },
          ]
        : []),
        
        {
          field: "pointOfSale",
          headerName: formatMessage({ id: "operation.fields.PointOfSale" }),
          flex: 1,
          renderCell(params) {            
            return (
              <Typography
              fontFamily="Poppins"
              fontSize="14px"
              fontWeight={500}
              color="#111827"
              >
              {pointsOfSale.find((pointOfSale) => pointOfSale.value === params
                .row.pointOfSaleId)?.label}
            </Typography>
          );
        },
      },
      {
        field: "operationDate",
        headerName: formatMessage({ id: "operation.fields.operationDate" }),
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell(params) {
          return (
            <Typography
            
            fontSize="14px"
            fontWeight={500}
            color="#111827"
            >
              {getFormalDate(params.row.operationDate)}
            </Typography>
          );
        },
      },
      {
        field: "amount",
        headerName: formatMessage({ id: "operation.fields.amount" }),
        flex: 1,
        align: "right",
        headerAlign: "right",
        renderCell(params) {
          const formattedAmount = new Intl.NumberFormat("es-ES", {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true,
          }).format(params.row.amount);
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {formattedAmount}
            </Typography>
          );
        },
      },
      {
        field: "status",
        headerName: formatMessage({ id: "operation.fields.Status" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {formatMessage({ id: `operation.status.${params.row.status}`})}
            </Typography>
          );
        },
      },
      {
        field: "clientName",
        headerName: formatMessage({ id: "operation.fields.ClientName" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.clientName}
            </Typography>
          );
        },
      },
      {
        field: "documentNumber",
        headerName: formatMessage({ id: "operation.fields.DocumentNumber" }),
        flex: 1,
        renderCell(params) {
          return (
            <Typography
              
              fontSize="14px"
              fontWeight={500}
              color="#111827"
            >
              {params.row.documentNumber}
            </Typography>
          );
        },
      },

      {
        field: "action",
        width: 80,
        type: "actions",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<EditIcon fontSize="small" />}
            onClick={() => onEdit(params.row.id)}
            label="Editar"
            placeholder={"Editar"}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            onClick={() => onDeleteOpenModal(params.row.id)}
            label="Borrar"
            placeholder={"Borrar"}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          />,
        ],
      },
      
      
    ],
    [IMAGE_SERVER, onEdit, companies, locale]
  );

  return (
    <Box className={styles.DataGridContainer}>
      <Modal.DeleteModal
        open={openDeleteModal}
        title={formatMessage({ id: "operation.deleteMessage" })}
        onAcceptHandler={onAcceptDeleteModal}
        onCancelHandler={onCloseDeleteModal}
      />
      <DataGrid
        rows={rows}
        columns={columns}
        pagination
        pageSize={pageSize}
        page={pageIndex}
        disableSelectionOnClick
        paginationMode="server"
        onPageChange={(newPage) => {
          handleChangePage(newPage);
        }}
        onPageSizeChange={(newPageSize) => handleChangeRowsPerPage(newPageSize)}
        sortModel={sort}
        onSortModelChange={(newSortModel) => handleSort(newSortModel)}
        hideFooter={rows.length === 0}
        autoHeight={rows.length !== 0}
        rowsPerPageOptions={[10, 20, 25]}
        rowCount={paginatedQuery?.total ?? 0}
        localeText={localeText}
        rowHeight={80}
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "#718096",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: 'none',
          },
          "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon": {
            display: 'none',
          },
          "& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-virtualScrollerRenderZone": {
            padding: "0px 0px"
          },
        }}
        loading={isLoading}
      />
      
    </Box>
  );
};
